/**
 * The reading screen (#573): the learner's PDF is being read in the background.
 *
 * Honest about progress, and about nothing else: the pages read and the pages in the upload, as two
 * separate numbers, plus one tile per page. It promises no time: when two or more pages have been read it may
 * say how long the rest is likely to take, measured from the pace so far (#619), and otherwise says nothing about
 * time. The ring is the registered proportion `pages-read`, tagged `data-proportion` (CLAUDE.md, history §18, §19): the machine's progress through a file, never
 * what the learner knows. It shows the two counts and never a `%`, is `aria-hidden` (the status line below carries the
 * words), and is not a `<progress>` or `<meter>`. The learner is told they may leave; the check goes on without them.
 */

import { Link } from 'react-router';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import type { ReadingEstimate } from './reading-estimate.js';
import { PageTiles, type PageTileModel } from './page-tiles.js';

export interface ReadingProgressProps {
  readonly pageCount: number;
  readonly tiles: readonly PageTileModel[];
  /** A measured estimate (#619), or null/absent when there is nothing sound to say about time. */
  readonly estimate?: ReadingEstimate | null;
}

export function ReadingProgress({ pageCount, tiles, estimate = null }: ReadingProgressProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const total = Math.max(pageCount, tiles.length);
  const read = tiles.filter((tile) => tile.state === 'read').length;
  const share = total === 0 ? 0 : (read / total) * 100;
  const ringStyle = {
    '--share': share,
    background: 'conic-gradient(var(--accent, var(--colour-action)) 0 calc(var(--share) * 1%), var(--colour-surface-subtle) calc(var(--share) * 1%) 100%)',
  };
  return (
    <>
      <h1 id="homework-reading">{t.readingTitle}</h1>
      <section className="hw-card hw-reading" aria-labelledby="homework-reading">
        <div className="hw-ring" data-proportion="pages-read" aria-hidden="true" style={ringStyle}>
          <div className="hw-ring-disc">
            <strong dir="ltr">
              {read}/{total}
            </strong>
            <span>{t.ringUnit}</span>
          </div>
        </div>
        <p role="status" className="visually-hidden">
          {t.pagesReadOf(read, total)}
        </p>
        <div className="hw-reading-text">
          {estimate !== null ? (
            <>
              <p className="hw-estimate">
                <strong>{t.timeLeft(estimate)}</strong>
              </p>
              <p className="hw-estimate-basis">{t.timeLeftBasis}</p>
            </>
          ) : null}
          <p className="hw-leave">
            <svg
              aria-hidden="true"
              focusable="false"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            {t.canLeave}
          </p>
          <Link className="hw-back" to="/homework">
            {t.backToHomework}
          </Link>
        </div>
      </section>
      <PageTiles tiles={tiles} total={total} />
    </>
  );
}
